import type { Balance } from '../data/schemas/balance';
import type { MoveData } from '../data/schemas/entities/move';
import type { SeededRng } from '../rng';
import type { ActivePokemon } from './types';

export interface DamageResult {
  amount: number;
  critical: boolean;
  stab: boolean;
}

export interface DamageRollCallbacks {
  onRngRoll: (purpose: string, drawIndex: number) => void;
}

/** See specs/battle/damage-formula.md */
export function computeDamage(
  attacker: ActivePokemon,
  defender: ActivePokemon,
  move: MoveData,
  balance: Balance['battle'],
  rng: SeededRng,
  callbacks: DamageRollCallbacks,
): DamageResult {
  if (move.power <= 0 || move.category === 'status') {
    return { amount: 0, critical: false, stab: false };
  }

  const levelFactor = Math.floor((2 * attacker.level) / 5) + 2;
  const attackStat =
    move.category === 'physical' ? attacker.stats.attack : attacker.stats.spAttack;
  const defenseStat =
    move.category === 'physical' ? defender.stats.defense : defender.stats.spDefense;
  const defense = Math.max(1, defenseStat);

  let value = Math.floor((levelFactor * move.power * attackStat) / defense / 50) + 2;

  const randSpan = balance.damageRandMax - balance.damageRandMin;
  const rand = balance.damageRandMin + randSpan * rng.next();
  callbacks.onRngRoll('damage-variance', rng.offset - 1);
  value = Math.floor(value * rand);

  let critical = false;
  if (rng.chance(balance.criticalHitChance)) {
    critical = true;
    callbacks.onRngRoll('critical-hit', rng.offset - 1);
    value = Math.floor(value * balance.criticalHitMultiplier);
  }

  const stab = attacker.types.includes(move.type);
  if (stab) {
    value = Math.floor(value * balance.stab);
  }

  const effectiveness = 1;
  value = Math.floor(value * effectiveness);

  const amount = Math.max(1, value);
  return { amount, critical, stab };
}
